import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { TenantContext } from "../../context/tenant/TenantContext";
import { loadGoogleAnalytics, removeGoogleAnalytics } from "../../utils/analytics";
import "./cookieconsent.css";

export default function CookieConsent() {
    const { tenant } = useContext(TenantContext);
    const [showBanner, setShowBanner] = useState(false);
    const location = useLocation();
    const tenantPath = location.pathname.split("/")[1];

    // Cookie consent expiration period - 6 months in milliseconds
    const CONSENT_EXPIRY_PERIOD = 6 * 30 * 24 * 60 * 60 * 1000;

    useEffect(() => {
        // Check if user has already made a choice and if it's still valid
        const cookieConsentData = localStorage.getItem("cookie-consent");
        
        if (cookieConsentData) {
            try {
                const { choice, timestamp } = JSON.parse(cookieConsentData);
                const currentTime = new Date().getTime();
                const timePassed = currentTime - timestamp;

                // If more than 6 months have passed, show banner again
                if (timePassed > CONSENT_EXPIRY_PERIOD) {
                    console.log("Cookie consent expired, showing banner again");
                    localStorage.removeItem("cookie-consent");
                    setTimeout(() => {
                        setShowBanner(true);
                    }, 1000);
                } else {
                    console.log("Cookie consent still valid");
                }
            } catch (e) {
                // If data is corrupted or in old format, remove it and show banner
                console.error("Invalid cookie consent data, clearing and showing banner");
                localStorage.removeItem("cookie-consent");
                setTimeout(() => {
                    setShowBanner(true);
                }, 1000);
            }
        } else {
            // No consent data found, show banner
            setTimeout(() => {
                setShowBanner(true);
            }, 1000);
        }
    }, []);

    const handleAcceptAll = () => {
        const consentData = {
            choice: "accepted",
            timestamp: new Date().getTime()
        };
        localStorage.setItem("cookie-consent", JSON.stringify(consentData));
        loadGoogleAnalytics();
        setShowBanner(false);
    };

    const handleDeny = () => {
        const consentData = {
            choice: "denied",
            timestamp: new Date().getTime()
        };
        localStorage.setItem("cookie-consent", JSON.stringify(consentData));
        removeGoogleAnalytics();
        setShowBanner(false);
    };

    const handleClose = () => {
        setShowBanner(false);
    };

    if (!showBanner) return null;

    return (
        <div className="cookie-consent-overlay">
            <div className="cookie-consent-banner">
                <div className="cookie-consent-left">
                    <div className="cookie-consent-icon">
                        <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
                            <circle cx="25" cy="25" r="25" fill="#FFDB65"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M37.8476 23.0022C37.948 23.654 38 24.3204 38 25C38 32.1748 32.1748 38 25 38C17.8252 38 12 32.1748 12 25C12 17.8252 17.8252 12 25 12C25.5551 12 26.1017 12.0351 26.6386 12.1028C26.9276 12.1391 27.1804 12.3108 27.3207 12.566C27.4597 12.8199 27.4694 13.1271 27.346 13.3895C27.1985 13.7015 27.1163 14.051 27.1163 14.4186C27.1163 15.2047 27.4924 15.9036 28.074 16.345C28.3546 16.5591 28.4864 16.917 28.4102 17.2629C28.3546 17.5156 28.3256 17.7768 28.3256 18.0465C28.3256 20.0491 29.9509 21.6744 31.9535 21.6744C32.0732 21.6744 32.1905 21.6684 32.3078 21.6575C32.6101 21.6285 32.9064 21.7518 33.0987 21.9876C33.5425 22.5342 34.2185 22.8837 34.9767 22.8837C35.5137 22.8837 36.0107 22.7084 36.4122 22.4121C36.6673 22.2234 37.0023 22.1811 37.2962 22.302C37.59 22.423 37.798 22.689 37.8476 23.0022ZM30.7442 27.1163C29.9098 27.1163 29.2326 27.7935 29.2326 28.6279C29.2326 29.4623 29.9098 30.1395 30.7442 30.1395C31.5786 30.1395 32.2558 29.4623 32.2558 28.6279C32.2558 27.7935 31.5786 27.1163 30.7442 27.1163ZM21.6744 30.1395C20.84 30.1395 20.1628 30.8167 20.1628 31.6512C20.1628 32.4856 20.84 33.1628 21.6744 33.1628C22.5088 33.1628 23.186 32.4856 23.186 31.6512C23.186 30.8167 22.5088 30.1395 21.6744 30.1395ZM21.9647 17.0791C21.464 17.0791 21.0577 17.4854 21.0577 17.986C21.0577 18.4867 21.464 18.893 21.9647 18.893C22.4653 18.893 22.8716 18.4867 22.8716 17.986C22.8716 17.4854 22.4653 17.0791 21.9647 17.0791ZM25.3023 27.1163C24.8017 27.1163 24.3953 27.5226 24.3953 28.0233C24.3953 28.5239 24.8017 28.9302 25.3023 28.9302C25.803 28.9302 26.2093 28.5239 26.2093 28.0233C26.2093 27.5226 25.803 27.1163 25.3023 27.1163ZM18.3488 23.4884C17.8482 23.4884 17.4419 23.8947 17.4419 24.3953C17.4419 24.896 17.8482 25.3023 18.3488 25.3023C18.8495 25.3023 19.2558 24.896 19.2558 24.3953C19.2558 23.8947 18.8495 23.4884 18.3488 23.4884ZM24.093 20.7674C23.2586 20.7674 22.5814 21.4447 22.5814 22.2791C22.5814 23.1135 23.2586 23.7907 24.093 23.7907C24.9274 23.7907 25.6047 23.1135 25.6047 22.2791C25.6047 21.4447 24.9274 20.7674 24.093 20.7674Z" fill="#00BAA1"/>
                        </svg>
                    </div>
                    
                    <div className="cookie-consent-text">
                        <h5 className="cookie-consent-title">Your Privacy Matters</h5>
                        <p className="cookie-consent-message">
                            We use cookies to enhance your experience and analyze how our site is used.
                        </p>
                        <Link 
                            to={`/${tenantPath}/cookie-policy`} 
                            className="cookie-consent-link"
                        >
                            Read Cookie Policy
                        </Link>
                    </div>
                </div>

                <div className="cookie-consent-actions">
                    <Button 
                        variant="secondary" 
                        className="cookie-consent-btn-accept" 
                        onClick={handleAcceptAll}
                    >
                        Accept All Cookies
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        className="cookie-consent-btn-deny"
                        onClick={handleDeny}
                    >
                        Deny
                    </Button>
                    <button 
                        className="cookie-consent-close" 
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        <svg width="24" height="24" viewBox="0 0 18 18" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M5.29289 4.29289C5.68342 3.90237 6.31658 3.90237 6.70711 4.29289L9 6.58579L11.2929 4.29289C11.6834 3.90237 12.3166 3.90237 12.7071 4.29289C13.0976 4.68342 13.0976 5.31658 12.7071 5.70711L10.4142 8L12.7071 10.2929C13.0976 10.6834 13.0976 11.3166 12.7071 11.7071C12.3166 12.0976 11.6834 12.0976 11.2929 11.7071L9 9.41421L6.70711 11.7071C6.31658 12.0976 5.68342 12.0976 5.29289 11.7071C4.90237 11.3166 4.90237 10.6834 5.29289 10.2929L7.58579 8L5.29289 5.70711C4.90237 5.31658 4.90237 4.68342 5.29289 4.29289Z" fill="black"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
